import { Suspense, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation, useOutletContext, useParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { hasInternalToolRouteAccess } from '@/lib/internalToolAccess'
import { internalTools } from '@/lib/internalToolRegistry'
import { identifyUser } from '@/lib/logrocket'
import { LoginPage, LoadingScreen } from '@/components/auth'
import { MainLayout } from '@/components/layout'
import { Skeleton } from '@/components/ui'
import { AnnouncementRulesPage } from '@/pages/AnnouncementRules'
import { AssistantBridgeUsagePage } from '@/pages/AssistantBridgeUsage'
import { BillingCashPage } from '@/pages/BillingCash'
import { DefaultAssociateNamePage } from '@/pages/DefaultAssociateName'
import { HomePage } from '@/pages/Home'
import { OfficeHoursPage } from '@/pages/OfficeHours'
import {
  LLMEvaluationLayout,
  AnalysisList,
  AnalysisDetail,
  PromptEditor,
  ComparisonView,
  RenewalHealthLayout,
  RenewalHealthOverview,
  RenewalHealthDetail,
  ChargeCodeMappingsPage,
  ChargeCodeExplorerPage,
  ActiveProductsPage,
  AccountSlackNotificationsPage,
  AssistantBridgePackagesPage,
  AccountManagementAccountDetail,
  AccountManagementLayout,
  AccountManagementOverview,
  AccountManagementPortfolioBreakdown,
  YardiEtlGeneratorPage,
  BusinessReviewRoiPage,
  SnowflakeMrrByCustomerPage,
  RouteActivityPage,
  ConversationQAPage,
  ConversationQAQualityPage,
  FrontEmailListsPage,
  EntrataIntegrationDashboardPage,
  AppWorkResidentsPage,
  PropertyDataHealthPage,
  PortfolioAssignmentsPage,
  PortfolioOnboardingPage,
  LeasingManagementPage,
  SurveyControlLayout,
  SurveyControlOverview,
  SurveyControlAccountDetail,
  SurveyControlPropertyDetail,
  SurveyControlSettingDetail,
  SurveyControlBulkOperations,
  PropertyFaqCollectorLayout,
  PropertyFaqCollectorOverview,
  PropertyFaqCollectorAccountDetail,
  PropertyFaqCollectorRequestStatus,
  SurveyControlChangeLogs,
  SurveyControlRecords,
  LeaseAuditReviewQueuePage,
  LeaseAuditReviewDecisionPage,
  AuditCharterListPage,
  AuditCharterWorkspacePage,
  AuditTrialLauncherPage,
  AuditTrialRunPage,
  PgBossDashboardPage,
  DogfoodPage,
  VoiceOpsPage,
  PublicMarkdownDocumentsPage,
  RecordPaymentsPage,
  KnowledgeGapsPage,
  LearnedFaqsPage,
  AnswerCardsPage,
} from '@/routes/lazyPages'

function RouteFallback({ label = 'Loading page...' }) {
  return (
    <div className="flex min-h-96 items-center justify-center px-6 py-10">
      <div className="w-full max-w-md space-y-3" role="status" aria-live="polite">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-20 w-full" />
        <p className="text-sm text-muted-foreground">{label}</p>
      </div>
    </div>
  )
}

function LazyRoute({ children, label }) {
  return <Suspense fallback={<RouteFallback label={label} />}>{children}</Suspense>
}

function ToolRouteGuard({ children }) {
  const { user } = useOutletContext()
  const location = useLocation()

  if (!hasInternalToolRouteAccess(location.pathname, internalTools, user)) {
    return <Navigate to="/" replace />
  }

  return children
}

function LegacyCharterRedirect() {
  const { charterId } = useParams()
  return <Navigate to={`/lease-audit-trials/setup/${charterId}`} replace />
}

function LegacyTrialRunRedirect() {
  const { auditId } = useParams()
  return <Navigate to={`/lease-audit-trials/run/${auditId}`} replace />
}

/**
 * Internal Dashboard App
 *
 * Authentication Flow:
 * 1. App loads → useAuth checks /api/internal/auth/me
 * 2. If loading → show LoadingScreen
 * 3. If not authenticated → show LoginPage
 * 4. If authenticated → show MainLayout with nested routes
 *
 * Layout Structure:
 * - MainLayout provides the global header, app launcher, and content area
 * - Pages render inside MainLayout via <Outlet />
 * - All pages share the same navigation
 *
 * Stack:
 * - Vite 8 (Rolldown bundler)
 * - React 19 + React Router v7
 * - TanStack Query (data fetching)
 * - TanStack Table (tables)
 * - shadcn/ui + Tailwind CSS
 * - Tremor (charts)
 * - react-hook-form + zod (forms)
 */

/**
 * Protected routes wrapper
 * Only renders children if user is authenticated
 */
function ProtectedRoutes() {
  const { user, isLoading, isAuthenticated, logout } = useAuth()

  useEffect(() => {
    if (isAuthenticated && user) identifyUser(user)
  }, [isAuthenticated, user])

  // Show loading screen while checking auth
  if (isLoading) {
    return <LoadingScreen />
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />
  }

  // User is authenticated - render dashboard with layout
  return (
    <Routes>
      {/* MainLayout wraps all authenticated routes */}
      <Route element={<MainLayout user={user} onLogout={logout} />}>
        <Route index element={<HomePage />} />

        {/* LLM Evaluation Tool */}
        <Route
          path="llm-evaluation"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading evaluations...">
                <LLMEvaluationLayout />
              </LazyRoute>
            </ToolRouteGuard>
          }
        >
          <Route
            index
            element={
              <LazyRoute label="Loading evaluations...">
                <AnalysisList />
              </LazyRoute>
            }
          />
          <Route
            path="prompts"
            element={
              <LazyRoute label="Loading prompt editor...">
                <PromptEditor />
              </LazyRoute>
            }
          />
          <Route
            path=":id"
            element={
              <LazyRoute label="Loading analysis...">
                <AnalysisDetail />
              </LazyRoute>
            }
          />
          <Route
            path=":id/compare"
            element={
              <LazyRoute label="Loading comparison...">
                <ComparisonView />
              </LazyRoute>
            }
          />
        </Route>

        {/* Renewal Health Tool */}
        <Route
          path="renewal-health"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading renewal health...">
                <RenewalHealthLayout />
              </LazyRoute>
            </ToolRouteGuard>
          }
        >
          <Route
            index
            element={
              <LazyRoute label="Loading renewal health...">
                <RenewalHealthOverview />
              </LazyRoute>
            }
          />
          <Route
            path=":accountId"
            element={
              <LazyRoute label="Loading account detail...">
                <RenewalHealthDetail />
              </LazyRoute>
            }
          />
        </Route>

        {/* Charge Code Explorer */}
        <Route
          path="charge-code-explorer"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading charge code explorer...">
                <ChargeCodeExplorerPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />
        {/* Charge Code Mappings (Lease Audit) */}
        <Route
          path="charge-code-mappings"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading charge code mappings...">
                <ChargeCodeMappingsPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* Active Products */}
        <Route
          path="active-products"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading active products...">
                <ActiveProductsPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* Account Slack Notifications */}
        <Route
          path="account-slack-notifications"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading account Slack notifications...">
                <AccountSlackNotificationsPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* AssistantBridge MCP Packages */}
        <Route
          path="assistant-bridge-packages"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading AssistantBridge MCP package setup...">
                <AssistantBridgePackagesPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* AssistantBridge MCP Usage */}
        <Route
          path="assistant-bridge-usage"
          element={
            <ToolRouteGuard>
              <AssistantBridgeUsagePage />
            </ToolRouteGuard>
          }
        />

        {/* Account Management Dashboard */}
        <Route
          path="account-management"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading account management...">
                <AccountManagementLayout />
              </LazyRoute>
            </ToolRouteGuard>
          }
        >
          <Route
            index
            element={
              <LazyRoute label="Loading account overview...">
                <AccountManagementOverview />
              </LazyRoute>
            }
          />
          <Route
            path="portfolios"
            element={
              <LazyRoute label="Loading portfolio breakdown...">
                <AccountManagementPortfolioBreakdown />
              </LazyRoute>
            }
          />
          <Route
            path="accounts/:accountId"
            element={
              <LazyRoute label="Loading account detail...">
                <AccountManagementAccountDetail />
              </LazyRoute>
            }
          />
        </Route>

        {/* Yardi ETL Generator */}
        <Route
          path="yardi-etl-generator"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading Yardi ETL generator...">
                <YardiEtlGeneratorPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* Business Review ROI workbook */}
        <Route
          path="business-review-roi"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading ROI workbook builder...">
                <BusinessReviewRoiPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* Customer MRR */}
        <Route
          path="customer-mrr"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading customer MRR...">
                <SnowflakeMrrByCustomerPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* Billing and cash */}
        <Route
          path="record-payments"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading record invoice payments...">
                <RecordPaymentsPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />
        <Route
          path="billing-cash"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading billing and cash...">
                <BillingCashPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />
        <Route
          path="snowflake-mrr"
          element={
            <ToolRouteGuard>
              <Navigate to="/customer-mrr" replace />
            </ToolRouteGuard>
          }
        />

        {/* Dashboard Usage And Exports */}
        <Route
          path="route-activity"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading dashboard usage and exports...">
                <RouteActivityPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* Conversation QA: overview (/conversation-qa) and associate detail (/conversation-qa/:associateId) */}
        <Route
          path="conversation-qa"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading conversation QA...">
                <ConversationQAPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />
        <Route
          path="conversation-qa/:associateId"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading associate QA...">
                <ConversationQAPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />
        <Route
          path="conversation-qa/:associateId/quality"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading quality detail...">
                <ConversationQAQualityPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* Default Associate Name */}
        <Route
          path="default-associate-name"
          element={
            <ToolRouteGuard>
              <DefaultAssociateNamePage />
            </ToolRouteGuard>
          }
        />

        {/* Knowledge Gaps */}
        <Route
          path="knowledge-gaps"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading knowledge gaps...">
                <KnowledgeGapsPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* Learned FAQs */}
        <Route
          path="learned-faqs"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading learned FAQs...">
                <LearnedFaqsPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* Answer cards */}
        <Route
          path="answer-cards"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading answer cards...">
                <AnswerCardsPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* Front Email Lists */}
        <Route
          path="front-email-lists"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading Front email lists...">
                <FrontEmailListsPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        <Route
          path="appwork-residents"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading AppWork residents...">
                <AppWorkResidentsPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* Entrata Resident Report */}
        <Route
          path="entrata-integration-dashboard"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading Entrata resident report...">
                <EntrataIntegrationDashboardPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* After-hours and office hours */}
        <Route
          path="office-hours"
          element={
            <ToolRouteGuard>
              <OfficeHoursPage />
            </ToolRouteGuard>
          }
        />

        <Route
          path="property-data-health"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading property data health...">
                <PropertyDataHealthPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* Portfolio Assignments */}
        <Route
          path="portfolio-assignments"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading portfolio assignments...">
                <PortfolioAssignmentsPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* Portfolio Onboarding */}
        <Route
          path="portfolio-onboarding"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading portfolio onboarding...">
                <PortfolioOnboardingPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* Leasing AI Onboarding */}
        <Route
          path="leasing-management"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading leasing AI onboarding...">
                <LeasingManagementPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* Survey Control — admin surface for survey configuration */}
        <Route
          path="survey-control"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading survey control...">
                <SurveyControlLayout />
              </LazyRoute>
            </ToolRouteGuard>
          }
        >
          <Route
            index
            element={
              <LazyRoute label="Loading survey control...">
                <SurveyControlOverview />
              </LazyRoute>
            }
          />
          <Route
            path="bulk"
            element={
              <LazyRoute label="Loading bulk operations...">
                <SurveyControlBulkOperations />
              </LazyRoute>
            }
          />
          <Route
            path="change-logs"
            element={
              <LazyRoute label="Loading change logs...">
                <SurveyControlChangeLogs />
              </LazyRoute>
            }
          />
          <Route
            path="setting/:settingId"
            element={
              <LazyRoute label="Loading setting...">
                <SurveyControlSettingDetail />
              </LazyRoute>
            }
          />
          <Route
            path=":accountId/records"
            element={
              <LazyRoute label="Loading survey records...">
                <SurveyControlRecords />
              </LazyRoute>
            }
          />
          <Route
            path=":accountId/property/:portfolioId"
            element={
              <LazyRoute label="Loading property...">
                <SurveyControlPropertyDetail />
              </LazyRoute>
            }
          />
          <Route
            path=":accountId"
            element={
              <LazyRoute label="Loading account...">
                <SurveyControlAccountDetail />
              </LazyRoute>
            }
          />
        </Route>

        {/* Property FAQ Collector — internal admin surface that replaces the
            legacy Retool app for ingesting property documents and FAQs. */}
        <Route
          path="property-faq-collector"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading property FAQ collector...">
                <PropertyFaqCollectorLayout />
              </LazyRoute>
            </ToolRouteGuard>
          }
        >
          <Route
            index
            element={
              <LazyRoute label="Loading property FAQ collector...">
                <PropertyFaqCollectorOverview />
              </LazyRoute>
            }
          />
          <Route
            path="request-status"
            element={
              <LazyRoute label="Loading request status...">
                <PropertyFaqCollectorRequestStatus />
              </LazyRoute>
            }
          />
          <Route
            path=":accountId"
            element={
              <LazyRoute label="Loading account...">
                <PropertyFaqCollectorAccountDetail />
              </LazyRoute>
            }
          />
        </Route>

        {/* Public Markdown — create and update AI-readable product guides */}
        <Route
          path="public-markdown-documents"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading public Markdown editor...">
                <PublicMarkdownDocumentsPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* Announcement Rules — internal guardrails for customer announcement review */}
        <Route
          path="announcement-rules"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading announcement rules...">
                <AnnouncementRulesPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* Lease Audit Review — internal review queue + decision flow */}
        <Route
          path="lease-audit-review"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading lease audit review...">
                <LeaseAuditReviewQueuePage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />
        <Route
          path="lease-audit-review/:discrepancyId"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading discrepancy...">
                <LeaseAuditReviewDecisionPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* Legacy Audit Charter links redirect into the single trial flow. */}
        <Route
          path="lease-audit-charters"
          element={
            <ToolRouteGuard>
              <Navigate to="/lease-audit-trials" replace />
            </ToolRouteGuard>
          }
        />
        <Route
          path="lease-audit-charters/:charterId"
          element={
            <ToolRouteGuard>
              <LegacyCharterRedirect />
            </ToolRouteGuard>
          }
        />

        {/* Lease Audit Trials — property setup through persisted results. */}
        <Route
          path="lease-audit-trials"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading Lease Audit Trials...">
                <AuditCharterListPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />
        <Route
          path="lease-audit-trials/setup/:charterId"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading Lease Audit Trial setup...">
                <AuditCharterWorkspacePage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />
        <Route
          path="lease-audit-trials/preview/:charterId"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading Lease Audit sample...">
                <AuditTrialLauncherPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />
        <Route
          path="lease-audit-trials/run/:auditId"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading Lease Audit trial...">
                <AuditTrialRunPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />
        <Route
          path="lease-audit-trials/:auditId"
          element={
            <ToolRouteGuard>
              <LegacyTrialRunRedirect />
            </ToolRouteGuard>
          }
        />

        {/* Queue Console — internal pg-boss job inspection */}
        <Route
          path="pg-boss-dashboard"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading queue console...">
                <PgBossDashboardPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* Voice Mode Dogfood — staff-only realtime leasing voice agent */}
        <Route
          path="voice-mode"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading voice mode…">
                <DogfoodPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* Voice Ops — voice assistant performance, cost, and failures */}
        <Route
          path="voice-ops"
          element={
            <ToolRouteGuard>
              <LazyRoute label="Loading voice ops…">
                <VoiceOpsPage />
              </LazyRoute>
            </ToolRouteGuard>
          }
        />

        {/* Catch-all for unknown routes */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}

export default function App() {
  return <ProtectedRoutes />
}
